import React from 'react'

import {useNavigate} from 'react-router-dom'

import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import LinearProgress from '@mui/material/LinearProgress';

import Pagination from '@mui/material/Pagination';
import Box from '@mui/material/Box';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import ASSIGNARTimesheetsEmployeeTable from './ASSIGNARTimesheetsEmployeeTable.jsx'

import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';

export default function ASSIGNARTimesheetsFull({ division = "", bearerToken }) {
  // State handling on component render
  const [timesheetData, setTimesheetData] = React.useState([]);

  // Search Functions
  const [searchTerm, setSearchTerm] = React.useState("");
  const [filteredTimesheetData, setFilteredTimesheetData] = React.useState([]);

  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    if (searchTerm !== "") {
        const results = timesheetData.filter(timesheet =>
            Object.values(timesheet).some(val =>
              val.toString().toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
        setFilteredTimesheetData(results);
    } else {
        setFilteredTimesheetData(timesheetData);
    }
    setPage(1)
  }, [searchTerm, timesheetData]);

  // Date Functions
  function getPastMonthDateRanges() {
    const dateRanges = [];
    const curr = new Date();
    curr.setDate(curr.getDate() - curr.getDay() + 1);  // set to previous Monday

    for (let i = 0; i < 4; i++) {
      const firstDay = new Date(curr);
      firstDay.setDate(curr.getDate() - 7 * i);  // Move back i weeks
      const lastDay = new Date(firstDay);
      lastDay.setDate(firstDay.getDate() + 6);  // Add 6 days to get to Sunday

      // Format dates to "YYYY-MM-DD"
      const firstDayStr = `${firstDay.getFullYear()}-${String(firstDay.getMonth() + 1).padStart(2, '0')}-${String(firstDay.getDate()).padStart(2, '0')}`;
      const lastDayStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;

      // Add date range to array
      dateRanges.push(`${firstDayStr} - ${lastDayStr}`);
    }

    return dateRanges;
  }
  const [selectedDate, setSelectedDate] = React.useState("");
  const [dates, setDates] = React.useState(getPastMonthDateRanges());
  const [showWeekSelector, setShowWeekSelector] = React.useState(false)

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // January is 0!
    const year = date.getFullYear();
    return day + '/' + month + '/' + year;
  }

  // Pagination
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const navigate = useNavigate()

  // Initialize start and end dates
  const [startDate, setStartDate] = React.useState(getMondayOfLastWeek());
  const [endDate, setEndDate] = React.useState(getSundayOfLastWeek());

  function getMondayOfLastWeek() {
    let date = new Date();
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7) - 7);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  function getSundayOfLastWeek() {
    let date = new Date();
    let daysToSubtract = date.getDay() === 0 ? 7 : date.getDay();
    date.setDate(date.getDate() - daysToSubtract);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  const getTimesheet = async (pageNum) => {
    return fetch(`https://api.assignar.com.au/v2/timesheets?submitted_date__gte=${startDate}&submitted_date__lte=${endDate}&sort=submitted_date,id&page=${pageNum}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${bearerToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        return data;
      })
      .catch(error => {
        console.log('An error occurred: ', error)
      });
  }

  const getAllTimesheets = async () => {
    let i = 1;
    let data = await getTimesheet(i);
    let timesheetList = [];

    //console.log(JSON.stringify(data.data[0].start_time))
    while (data.data.length !== 0) {
      const timesheets = data.data.map(entry => {
        return {
          FullName: entry.user.full_name,
          StartTime: entry.start_time,
          EndTime: entry.end_time,
          BreakTime: entry.break_time,
          TotalHours: entry.total_time,
          Date: entry.submitted_date,
          TimesheetID: entry.id,
          UserID: entry.user_id,
        };
      });

      timesheetList = [...timesheetList, ...timesheets];
      i += 1;
      data = await getTimesheet(i);
    }

    setTimesheetData(timesheetList);
    setFilteredTimesheetData(timesheetList);

    setLoading(false)
  }

  React.useEffect(() => {
    getAllTimesheets();
  }, [startDate, endDate]);

  // Functions for converting time and calculating total hours
  const timeToMinutes = (timeString) => {
    if (timeString === '0' || timeString === '00') return 0;
    const [hours, minutes] = timeString.split(':');
    return parseInt(hours) * 60 + parseInt(minutes);
  };

  const minutesToTime = (minutes) => {
    let hours = Math.floor(minutes / 60);
    let mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const groupedByName = React.useMemo(() => {
    if (filteredTimesheetData.length === 0) {
      return {};
    }
    
    return filteredTimesheetData.reduce((acc, cur) => {
      let fullName = cur.FullName;
      if (!acc[fullName]) {
        acc[fullName] = [];
      }
      acc[fullName].push(cur);
      return acc;
    }, {});
  }, [filteredTimesheetData]);

  const handleExport = async () => {
   const rowData = {...groupedByName};
    
    const sortedNames = Object.keys(rowData).sort((a, b) => a.localeCompare(b));
    
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Exported Data');

    // Headers (same as before)
    const headers = ["FullName"];
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    days.forEach(day => {
        headers.push(day, `${day} Start`, `${day} Finish`, `${day} Lunch`, `${day} TotalHours`);
    });
    headers.push("TOTAL HOURS PER DAY");
    worksheet.columns = headers.map(header => ({ header, key: header, width: 15 }));

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
        cell.font = { bold: true };
    });
    
    // Step 1: Populating the sheet
    sortedNames.forEach(name => {
        const entries = rowData[name];
        const row = {};

        row["FullName"] = name;

        // Object to temporarily store aggregated data
        const aggregatedData = {};

        entries.forEach(entry => {
            const dayName = new Date(entry.Date).toLocaleString('en-US', { weekday: 'long' });

            // Initialize if not set
            if (!aggregatedData[dayName]) {
                aggregatedData[dayName] = {
                    Date: entry.Date,
                    StartTimes: [],
                    EndTimes: [],
                    BreakTime: 0,
                    TotalHours: 0
                };
            }

            // Append times
            aggregatedData[dayName].StartTimes.push(entry.StartTime);
            aggregatedData[dayName].EndTimes.push(entry.EndTime);

            // Aggregate break time and total hours
            aggregatedData[dayName].BreakTime += entry.BreakTime;
            const hours = parseInt(entry.TotalHours.split(":")[0]);
            const minutes = parseInt(entry.TotalHours.split(":")[1]);
            aggregatedData[dayName].TotalHours += hours + (minutes / 60);
        });

        // Populate the row with aggregated data
        for (const dayName in aggregatedData) {
            const data = aggregatedData[dayName];
            row[dayName] = data.Date.slice(8, 10) + "/" + data.Date.slice(5, 7);
            row[`${dayName} Start`] = data.StartTimes.join(", ");
            row[`${dayName} Finish`] = data.EndTimes.join(", ");
            row[`${dayName} Lunch`] = data.BreakTime.toString();
            row[`${dayName} TotalHours`] = Math.floor(data.TotalHours).toString().padStart(2, '0') + ':' + Math.round((data.TotalHours % 1) * 60).toString().padStart(2, '0');
        }

        worksheet.addRow(row);
    });

    // Step 2: Adding a formula for TOTAL HOURS PER DAY
    let totalHoursColumnNumber = worksheet.getColumn('TOTAL HOURS PER DAY').number;
    
    // Format column correctly:
    let totalTimeColumnFormat = worksheet.getColumn('TOTAL HOURS PER DAY');
    totalTimeColumnFormat.numFmt = '[HH]:MM';

    worksheet.eachRow({ includeEmpty: false }, function(row, rowNumber) {
      if(rowNumber !== 1) { // Exclude header row
        let formula = "=SUM(VALUE(F" + rowNumber + "),VALUE(K" + rowNumber + "),VALUE(P" + rowNumber + "),VALUE(U" + rowNumber + "),VALUE(Z" + rowNumber + "),VALUE(AE" + rowNumber + "),VALUE(AJ" + rowNumber + "))";;
        row.getCell(totalHoursColumnNumber).value = { formula: formula };
      }
    });

    // Step 3: Coloring columns
    // Define your colors
    const dayColors = {
      'Monday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF96ADC8' } },
      'Tuesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD7FFAB' } },
      'Wednesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCFF6C' } },
      'Thursday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD89D6A' } },
      'Friday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFA6B8' } },
      'Saturday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFD28D' } },
      'Sunday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3CCEC' } },
    };

    const darkerShades = {
      'Monday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF768A9F' } },
      'Tuesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB8E08A' } },
      'Wednesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E259' } },
      'Thursday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB07757' } },
      'Friday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3748F' } },
      'Saturday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2B76B' } },
      'Sunday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC2A9C9' } },
    };

    // Color the columns
      // Function to apply column color
    function colorColumn(colNumber, color) {
      worksheet.eachRow((row, rowNumber) => {
        if (row.getCell(colNumber)) {
          row.getCell(colNumber).fill = color;
        }
      });
    }

    // Applying column colors based on the day
    days.forEach((day, index) => {
      const startColNumber = 2 + index * 5; // starting from the 4th column (0-based index) and then skipping 5 columns for each day
      for (let i = 0; i < 4; i++) { // For 4 columns of the same day (Date, Start, Finish, Lunch)
        colorColumn(startColNumber + i, dayColors[day]);
      }
      colorColumn(startColNumber + 4, darkerShades[day]); // For the TotalHours column
    });

    // Color the "TOTAL HOURS PER DAY" column
    const totalHoursColumn = worksheet.getColumn(headers.indexOf("TOTAL HOURS PER DAY") + 1);
    totalHoursColumn.eachCell((cell) => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFF6347' }  // HEX for tomato color
        };
    });

    // Create a map of unique employee names to colors
    const nameToColorMap = {};
    let currentColorIndex = 0;
    const fullNameColumn = worksheet.getColumn(headers.indexOf("FullName") + 1);
    fullNameColumn.eachCell((cell, rowNumber) => {
        if (rowNumber !== 1) { // Exclude header row
            const employeeName = cell.value;
            if (!nameToColorMap[employeeName]) {
                let dayName = days[currentColorIndex % 7]; // Use modulo to cycle through days
                let color = darkerShades[dayName];
                if (currentColorIndex >= 7) {
                    // Increase the middle digit in ARGB to get a different shade.
                    let argb = color.fgColor.argb;
                    let digitToIncrement = parseInt(argb.slice(3, 4), 16); // Slice the 3rd character from the ARGB
                    digitToIncrement = (digitToIncrement + 1) % 16; // Increment and keep it below 16 (Hexadecimal)
                    color.fgColor.argb = argb.slice(0, 3) + digitToIncrement.toString(16) + argb.slice(4);
                }
                nameToColorMap[employeeName] = color;
                currentColorIndex++;
            }
        }
    });

    // Now, apply the colors based on the map
    fullNameColumn.eachCell((cell, rowNumber) => {
        if (rowNumber !== 1) { // Exclude header row
            cell.fill = nameToColorMap[cell.value];
        }
    });

    // Saving the sheet
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `new.xlsx`);
  };

  return (
    <>
      {division !== "" &&
        <>
        <div className ="page-buttons-bar page-buttons-bar-curved">
          <Button sx ={{color: "white"}} onClick={()=>{navigate('/')}}> <ArrowBackOutlinedIcon sx={{marginRight: "5px", fontSize:"1rem"}}/> back</Button>
          <div className="page-function-buttons-container">
            <Button sx ={{color: "white"}} onClick={()=>{setShowWeekSelector(!showWeekSelector)}}> <SearchIcon sx={{marginRight: "5px", fontSize:"1rem"}}/> Search</Button>
            <Button sx ={{color: "white"}} onClick={()=>{getAllTimesheets()}}> <RefreshOutlinedIcon sx={{marginRight: "5px", fontSize:"1rem"}}/> Refresh</Button>
          </div>
        </div>
        </>
      }

      { division === "" &&
        <div className ="page-buttons-bar">
          {division!=="" ? 
          <Button sx ={{color: "white"}} onClick={()=>{navigate('/')}}> <ArrowBackOutlinedIcon sx={{marginRight: "5px", fontSize:"1rem"}}/> back</Button>
          : 
          <div className='page-buttons-bar-subtitle' style={{paddingBottom: "1vh"}}>ASSIGNAR</div>
          }
        </div>
      }

      {
        showWeekSelector &&
        <>
          <div className='page-searchbar-box'>
            <TextField 
              label="Search"
              onChange={event => setSearchTerm(event.target.value)}
              sx={{
                  width: "40%",
                  color: 'tomato',
                  '& label.Mui-focused': {
                      color: 'tomato',
                  },
                  '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                          borderColor: 'tomato',
                      },
                  },
              }}
            />

            <Autocomplete
              value={selectedDate}
              options={dates}
              sx ={{width: "25%"}}
              onChange={(event, newValue) => {
                setSelectedDate(newValue);
                //console.log(newValue);
                if (newValue) {
                  const dateRange = newValue.split(' - ');
                  setStartDate(dateRange[0]);
                  setEndDate(dateRange[1]);
                } else {
                  setStartDate(getMondayOfLastWeek())
                  setEndDate(getSundayOfLastWeek())
                }
              }}
              renderInput={(params) => 
                <TextField 
                  {...params} 
                  label="Date" 
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: 'tomato',
                      },
                    },
                    '& .MuiInputLabel-outlined': {
                      '&.Mui-focused': {
                        color: 'tomato',
                      },
                    },
                  }}
                />
              }
            />
          </div>
        </>
      }

      <div className="timesheet-card">

        <div className="timesheet-card-title">
          Week: <span className='timesheet-card-text-orange'>{formatDate(startDate)} - {formatDate(endDate)}</span>
          <Button 
            sx={{
              float: "right",
              marginRight: "1vw",
              background: "#4CAF50",
              color: "white",
              border: "1px solid #4CAF50",
              "&:hover": {
                background: "#43A047",
                color: "white",
              }
            }}
            onClick={()=>handleExport()}
          >
            <FileDownloadIcon sx={{marginRight: "5px", fontSize:"1rem"}}/> Export
          </Button>
          <div>
          {
            loading &&
            <LinearProgress
            sx={{
              background: "black", marginRight:"1vw", marginTop: "1vh", marginBottom: "1vh",
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'tomato',
              },
              
            }} />
          }
          </div>
        </div>
          <div className="traffic-timesheet-summary-container">
            <div className="timesheet-summary">
              <div className="traffic-timesheet-subtitle-container">
                <div className="timesheet-card-subtitle timesheet-card-subtitle-total">Name</div>
                <div className="timesheet-card-subtitle timesheet-card-subtitle-total">Total Hours</div>
              </div>
              <div className="traffic-timesheet-card-data">
              {Object.entries(groupedByName)
                .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                .map(([name, entries], idx) => {
                  let totalMinutes = entries.reduce((sum, entry) => {
                      return sum + timeToMinutes(entry.TotalHours);
                  }, 0);

                  let totalHours = minutesToTime(totalMinutes);

                  return (
                      <ASSIGNARTimesheetsEmployeeTable key={idx} name={name} totalHours={totalHours} employeeData={entries}/>
                  );
                })}
                <Box display="flex" justifyContent="center" marginTop={3}>
                  <Pagination 
                  count={Math.ceil(Object.entries(groupedByName).length / rowsPerPage)} 
                  page={page} 
                  onChange={handleChangePage}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'black',
                    },
                    '& .MuiPaginationItem-page.Mui-selected': {
                      backgroundColor: '#444444',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'darkred',
                      }
                    },
                    '& .MuiPaginationItem-page.Mui-selected.Mui-focusVisible': {
                      backgroundColor: 'darkred',
                    },
                    '& .MuiPaginationItem-page:hover': {
                      backgroundColor: '#ffe6e6',
                    }
                  }}
                  />
              </Box>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
